"use client";

/**
 * V1d Abilities tab — cream-gold editorial marketplace.
 *
 * A rotating featured banner sits on top (hand-written slides, each shown only
 * once the live catalog carries its ability), then the catalog splits into two
 * card sections: what's already working for you, and what's still on the shelf. Searching collapses both into a single
 * "Results" section and drops the banner.
 *
 * Every section shows six cards and hides the rest behind a "Show N more"
 * expander, so the page opens at a readable length however big the catalog gets.
 *
 * Install still flows through InstallConfirmModal (permissions consent matters).
 * Uninstall lives on the ability detail page, not here.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import type { CatalogAbility, CatalogAbilityDetail } from "@bash/shared";
import { InstallConfirmModal } from "@/components/abilities/InstallConfirmModal";
import { getCatalogAbility } from "@/lib/api/abilities";
import { getCachedDetail, setCachedDetail } from "@/lib/abilities/cache";
import { prefetchAbility } from "@/lib/abilities/prefetch";
import { VestraLogoMark } from "@/components/ui/VestraLogoMark";
import { ChevronDownIcon } from "@/components/icons/ChevronDownIcon";
import {
  AbilityCard,
  AbilityGildDefs,
  SECTION_CAP,
  TabSkeleton,
  WorksCoins,
  formatInstalls,
  invLine,
  type WorksCoinSrc,
} from "./cards";

interface Props {
  abilities: CatalogAbility[];
  loading: boolean;
  search: string;
  onAfterInstall: () => void;
}

// --------------------------------------------------------------------------
// Featured carousel. The slide copy is marketing surface authored locally, but
// a slide only renders once the live catalog carries its ability: the banner
// never advertises something you cannot open or install, and every number it
// shows comes from the real catalog row.
// --------------------------------------------------------------------------

interface FeaturedSlideAuthor {
  name: string;
  /** Logo type rendered inline before the author name. */
  logo: "vestra" | "image" | null;
  /** When `logo === 'image'`, the asset to render. */
  logoUrl?: string;
}

interface FeaturedSlide {
  slug: string;
  name: string;
  /** Serif italic run that trails the banner title. Sentence-cased and
   * punctuated, since it reads as a continuation of the name. */
  taglineDisplay: string;
  description: string;
  prompts: string[];
  /** Brand coins in the banner foot. Local assets, authored order preserved. */
  works?: WorksCoinSrc[];
  /** Inline SVG for the badge. Renders white on the brand-coloured badge. */
  iconSvg?: React.ReactNode;
  /** Full hero artwork rendered on the right side at ~280px wide,
   * bigger than the standalone badge. Use when the brand has its own
   * product image rather than just a wordmark. Overrides iconSvg. */
  heroImageUrl?: string;
  author: FeaturedSlideAuthor;
  category: string;
}

const ANTHROPIC: FeaturedSlideAuthor = {
  name: "Anthropic",
  logo: "image",
  logoUrl: "/landing-d6/logos/mk-anthropic.svg",
};

const FEATURED_SLIDES: FeaturedSlide[] = [
  {
    slug: "higgsfield",
    name: "Higgsfield",
    taglineDisplay: "your AI creative team.",
    description:
      "Cinematic video, on-brand imagery, voice-over, scripts, scene boards — the whole creative pipeline in one place. Drop a brief, get a campaign. Drop a still, get a film.",
    prompts: [
      "generate a 30-second pitch reel",
      "animate this still",
      "storyboard this doc",
    ],
    heroImageUrl: "/brand/higgsfield-hero.png",
    author: {
      name: "Higgsfield",
      logo: "image",
      logoUrl: "/brand/higgsfield.jpg",
    },
    category: "Creative",
  },
  {
    slug: "small-business",
    name: "Small Business",
    taglineDisplay: "your back office, handled.",
    description:
      "Cash flow, bills, invoices, payroll prep, month-end, hiring and contracts, run with real thresholds and your approval on anything that touches money or customers.",
    prompts: [
      "what does my cash look like for 90 days?",
      "chase the overdue invoices",
      "review this contract",
    ],
    heroImageUrl: "/brand/abilities/small-business-hero.webp",
    author: ANTHROPIC,
    category: "Business",
  },
  {
    slug: "sales",
    name: "Sales",
    taglineDisplay: "every call prepped, every deal honest.",
    description:
      "Researches the account, preps your calls, writes the follow-up, reviews your pipeline and keeps the CRM clean, citing every number it uses.",
    prompts: [
      "prep me for my 2pm with Acme",
      "draft the follow-up from this call",
      "which deals are slipping?",
    ],
    works: [
      { src: "/landing-d6/logos/salesforce.svg" },
      { src: "/landing-d6/logos/gmail.svg" },
      { src: "/landing-d6/logos/slack.svg" },
    ],
    heroImageUrl: "/brand/abilities/sales-hero.webp",
    author: ANTHROPIC,
    category: "Sales",
  },
  {
    slug: "growth-marketing",
    name: "Growth Marketing",
    taglineDisplay: "plans, ads and outreach that work.",
    description:
      "A 12-month plan sized to your budget, paid ads audited the way operators run them, cold emails people answer and press pitches journalists can use.",
    prompts: [
      "write our 12-month marketing plan",
      "audit our Google Ads",
      "draft a cold email to agencies",
    ],
    heroImageUrl: "/brand/abilities/growth-marketing-hero.webp",
    author: { name: "Corey Haines", logo: null },
    category: "Marketing",
  },
];

export function AbilitiesTab({
  abilities,
  loading,
  search,
  onAfterInstall,
}: Props) {
  const { user } = useAuth();
  const router = useRouter();
  const [installTarget, setInstallTarget] =
    useState<CatalogAbilityDetail | null>(null);
  const [installTargetLoading, setInstallTargetLoading] = useState<
    string | null
  >(null);
  // Banner for "couldn't load the ability detail" — the install flow fetches
  // the full CatalogAbilityDetail before opening its modal, and that can fail.
  const [actionError, setActionError] = useState<string | null>(null);

  const isFiltering = Boolean(search.trim());

  // Everything already doing something for the user, most-permanent first.
  const working = useMemo(() => {
    const order = { system: 0, personal: 1, installed: 2 } as const;
    return abilities
      .filter((a) => a.state !== "available")
      .sort(
        (a, b) =>
          order[a.state as keyof typeof order] -
          order[b.state as keyof typeof order]
      );
  }, [abilities]);

  // Everything you can install today, in catalog order.
  const shelf = useMemo(
    () => abilities.filter((a) => a.state === "available"),
    [abilities]
  );

  // A featured slide renders only while the catalog carries its ability, and
  // it carries that live row so the CTA and the numbers are real.
  const featured = useMemo(() => {
    const liveBySlug = new Map(abilities.map((a) => [a.slug, a]));
    return FEATURED_SLIDES.flatMap((slide) => {
      const live = liveBySlug.get(slide.slug);
      return live ? [{ slide, live }] : [];
    });
  }, [abilities]);

  // Searching flattens the split: a match is a match whatever its state. The
  // server already applied `q`, so every row it returned is a result.
  const results = abilities;

  async function handleInstallClick(ability: CatalogAbility) {
    // Anon visitors can browse the marketplace but must sign in to install.
    if (!user) {
      router.push("/login?next=/abilities");
      return;
    }
    setActionError(null);
    setInstallTargetLoading(ability.slug);
    const cached = getCachedDetail(ability.slug);
    if (cached) {
      setInstallTarget(cached);
    } else {
      try {
        const detail = await getCatalogAbility(ability.slug);
        setCachedDetail(ability.slug, detail);
        setInstallTarget(detail);
      } catch (e) {
        setActionError(
          e instanceof Error ? e.message : "Failed to load ability details"
        );
      }
    }
    setInstallTargetLoading(null);
  }

  if (loading && abilities.length === 0) {
    return <TabSkeleton />;
  }

  return (
    <div className="ab-scope [--ab-gold-line:color-mix(in_srgb,_var(--gold-molten-mid)_30%,_transparent)] [--ab-gold-wash:color-mix(in_srgb,_var(--gold-molten-mid)_7%,_transparent)] [--ab-plate-shadow:0_1px_2px_rgba(40,_30,_10,_0.05),_0_22px_48px_-30px_rgba(50,_38,_16,_0.25)] [--ab-card-shadow:0_1px_2px_rgba(40,_30,_10,_0.04),_0_14px_32px_-24px_rgba(50,_38,_16,_0.22)] [--ab-amber:#b45309]">
      {/* The gild gradient every stroked mark below references by id. */}
      <AbilityGildDefs />

      {actionError && (
        <div
          role="alert"
          className="mb-4 rounded-12 border border-status-error/30 bg-status-error/5
 px-4 py-3 text-3.5 text-status-error"
        >
          {actionError}
        </div>
      )}

      <div className="flex flex-col gap-y-11.5">
        {!isFiltering && featured.length > 0 && (
          <FeaturedCarousel
            featured={featured}
            installing={installTargetLoading}
            onInstall={handleInstallClick}
          />
        )}

        {isFiltering ? (
          results.length > 0 ? (
            <CardSection
              title="Results"
              rows={results}
              installing={installTargetLoading}
              onInstall={handleInstallClick}
              last
            />
          ) : (
            <EmptyState message={`No abilities match "${search}".`} />
          )
        ) : (
          <>
            <CardSection
              title="Working for"
              italicWord="you"
              rows={working}
              last={shelf.length === 0}
            />
            <CardSection
              title="From the"
              italicWord="marketplace"
              rows={shelf}
              installing={installTargetLoading}
              onInstall={handleInstallClick}
              last
            />
            {working.length === 0 && shelf.length === 0 && (
              <EmptyState message="No abilities yet. Install one from the catalog above, or build your own." />
            )}
          </>
        )}
      </div>

      <InstallConfirmModal
        open={!!installTarget}
        ability={installTarget}
        onCancel={() => setInstallTarget(null)}
        onSuccess={() => {
          setInstallTarget(null);
          onAfterInstall();
        }}
      />
    </div>
  );
}

// --------------------------------------------------------------------------
// CardSection — a serif head, a rule, a count, and at most six cards. The
// expander is one-way per mount: once you've asked to see the rest, the
// section stays open while you're on the page.
// --------------------------------------------------------------------------

function CardSection({
  title,
  italicWord,
  rows,
  installing,
  onInstall,
  last,
}: {
  title: string;
  italicWord?: string;
  rows: CatalogAbility[];
  installing?: string | null;
  onInstall?: (a: CatalogAbility) => void;
  /** True when no section renders below this one — the collapse gate only
   * exists to keep LATER sections reachable, so the last section renders in
   * full (founder, 08-12). */
  last?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (rows.length === 0) return null;
  const visible = expanded || last ? rows : rows.slice(0, SECTION_CAP);
  const hidden = rows.length - visible.length;
  return (
    <section className="ab-sect">
      <div className="ab-sect__head flex items-baseline gap-3 mb-4.5">
        <h2 className="ab-sect__title font-serif font-normal text-7 tracking-[-0.01em] text-fg-default whitespace-nowrap [&_em]:italic max-sm:text-6">
          {title} {italicWord ? <em>{italicWord}</em> : null}
        </h2>
        <span className="ab-sect__rule flex-1 h-px bg-border-subtle transform-[translateY(-6px)]" />
        <span className="ab-sect__count font-mono text-3 tracking-[0.08em] text-fg-subtle transform-[translateY(-4px)]">
          {rows.length}
        </span>
      </div>
      <div className="ab-grid grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-3 sm:gap-4">
        {visible.map((a) => (
          <AbilityCard
            key={a.id}
            ability={a}
            installing={installing === a.slug}
            onInstall={onInstall}
          />
        ))}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          className="ab-sect__more flex items-center justify-center gap-2 w-full mt-4 p-3 bg-none border border-dashed border-border-default rounded-12 font-mono text-3 tracking-[0.06em] text-fg-subtle cursor-pointer transition-colors hover:bg-bg-subtle hover:text-fg-default hover:border-ab-gold-line [&_svg]:w-3 [&_svg]:h-3 [&_svg]:[stroke:currentColor]"
          onClick={() => setExpanded(true)}
        >
          <span>Show {hidden} more</span>
          <ChevronDownIcon width={12} height={12} />
        </button>
      )}
    </section>
  );
}

// --------------------------------------------------------------------------
// FeaturedCarousel — the featured slides whose abilities are live. Auto-rotates
// every 4.5s. Hover pauses. Click a dot to jump. Cross-fade transition between
// slides.
// --------------------------------------------------------------------------

const ROTATE_MS = 4500;

function FeaturedCarousel({
  featured,
  installing,
  onInstall,
}: {
  featured: { slide: FeaturedSlide; live: CatalogAbility }[];
  installing: string | null;
  /** The grid cards' install flow (detail fetch → InstallConfirmModal) — the
   * banner CTA reuses it verbatim for live-but-not-installed slides. */
  onInstall: (a: CatalogAbility) => void;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = featured.length;
  const dragStartX = useRef<number | null>(null);
  // The catalog can shrink under a mounted carousel; never point past the end.
  const active = index % total;

  useEffect(() => {
    if (paused) return;
    // The crossfade is disabled under reduced motion (globals.css), so a
    // still-running interval would hard-cut the whole banner every 4.5s.
    // Stop rotating instead, the way the design contract does.
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const id = setTimeout(() => {
      setIndex((i) => (i + 1) % total);
    }, ROTATE_MS);
    return () => clearTimeout(id);
  }, [paused, total, index]);

  // Pointer-based swipe / drag — single handler covers both touch and
  // mouse / trackpad drag. Threshold 50px = intentional swipe, not a
  // jitter while clicking a button on the slide.
  //
  // setPointerCapture on the wrapper element so the pointerup always
  // lands here, even if the cursor leaves the carousel mid-drag or
  // releases on top of a child element (dot navigator, the CTA
  // button, the slide article itself). Without capture, the browser
  // delivers pointerup to whichever element is under the cursor at
  // release time, and the swipe handler silently no-ops because the
  // event never reaches the wrapper.
  //
  // Never capture a gesture that starts on a button, though: capture also
  // retargets the compatibility click to the capturing element, so the dots
  // stop advancing the carousel entirely. A swipe begun on a button still
  // works — pointerup bubbles from the button up to this wrapper.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartX.current = e.clientX;
    if ((e.target as HTMLElement).closest?.("button")) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore — some browsers refuse capture on non-primary pointers
    }
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore release errors
    }
    const start = dragStartX.current;
    dragStartX.current = null;
    if (start === null) return;
    const dx = e.clientX - start;
    if (Math.abs(dx) < 50) return;
    setIndex((i) => (dx > 0 ? (i - 1 + total) % total : (i + 1) % total));
  };

  // Trackpad horizontal scroll (two-finger swipe on a Mac) shows up as a
  // wheel event with deltaX, not a pointer drag. Translate accumulated
  // horizontal wheel delta into the same prev/next jump as a pointer swipe.
  const wheelAccum = useRef(0);
  const wheelLastFired = useRef(0);
  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Ignore predominantly-vertical scrolls so the page can still scroll
    // when the cursor happens to be over the carousel.
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    wheelAccum.current += e.deltaX;
    const now = Date.now();
    // Fire at most every 400ms so a continuous trackpad swipe doesn't
    // ping-pong through every slide.
    if (
      Math.abs(wheelAccum.current) > 60 &&
      now - wheelLastFired.current > 400
    ) {
      const dir = wheelAccum.current;
      wheelAccum.current = 0;
      wheelLastFired.current = now;
      setIndex((i) => (dir > 0 ? (i + 1) % total : (i - 1 + total) % total));
    }
  };

  return (
    <div
      className="ab-banner-wrap relative select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        dragStartX.current = null;
      }}
      onWheel={onWheel}
      // Behaviour the swipe handlers depend on: let the page scroll
      // vertically, and don't select banner copy mid-drag.
      style={{ touchAction: "pan-y" }}
    >
      {/*
 CSS-grid stack: every slide occupies the same grid cell (row 1,
 col 1) so the container's height fits the tallest slide and stays
 constant as we rotate. The .active class owns the crossfade.
 */}
      <div className="ab-banner-stack grid">
        {featured.map(({ slide, live }, i) => (
          <FeaturedSlideCard
            key={slide.slug}
            slide={slide}
            active={i === active}
            live={live}
            installing={installing === slide.slug}
            onInstall={onInstall}
          />
        ))}
      </div>

      <div className="ab-banner-dots absolute bottom-4 left-1/2 transform-[translateX(-50%)] flex gap-1.5 z-[2] [&_button]:w-1.5 [&_button]:h-1.5 [&_button]:rounded-100 [&_button]:border-none [&_button]:bg-border-default [&_button]:cursor-pointer [&_button]:p-0 [&_button]:transition-[width,background] duration-300 ease-out [&_button.on]:w-5.5 [&_button.on]:bg-gold-molten-mid">
        {featured.map(({ slide }, i) => (
          <button
            key={slide.slug}
            type="button"
            aria-label={`Show ${slide.name}`}
            onClick={() => setIndex(i)}
            className={i === active ? "on" : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function FeaturedSlideCard({
  slide,
  active,
  live,
  installing,
  onInstall,
}: {
  slide: FeaturedSlide;
  active: boolean;
  /** The real catalog row for this slide's slug. Drives a state-aware CTA:
   * not yet installed → the grid's install flow; installed → navigation. */
  live: CatalogAbility;
  installing: boolean;
  onInstall: (a: CatalogAbility) => void;
}) {
  const router = useRouter();
  const inv = invLine(live.counts);

  return (
    <article
      className={cn(
        "ab-banner [grid-area:1_/_1] relative overflow-hidden bg-bg-elevated border border-border-default rounded-24 shadow-ab-plate-shadow grid grid-cols-[minmax(0,_1fr)_auto] gap-8 items-center opacity-0 pointer-events-none transition-opacity duration-[450ms] max-md:grid-cols-[1fr] motion-reduce:transition-none",
        "max-md:pt-6.5 pt-8.5 pb-10 px-5 sm:px-6.5 md:pl-9.5 md:pr-11",
        "before:[content:''] before:absolute before:inset-0 before:bg-ab-gold-side before:pointer-events-none after:[content:''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:bg-gold-molten-sheen after:opacity-75",
        active ? "active opacity-100 pointer-events-auto" : ""
      )}
    >
      <div className="ab-banner__body relative min-w-0 flex flex-col gap-3">
        <div className="ab-banner__eyebrow flex items-center gap-2.5">
          <span className="ab-banner__cat font-mono text-2.5 tracking-[0.09em] uppercase text-fg-subtle">
            {slide.category}
          </span>
        </div>

        <h2 className="ab-banner__title text-7.5 font-semibold tracking-tight leading-[1.1] text-fg-default [&_.tag]:font-serif [&_.tag]:italic [&_.tag]:font-normal [&_.tag]:text-7 [&_.tag]:text-gold-ink [&_.tag]:tracking-normal [&_.tag]:ml-2.5 max-sm:text-6 max-sm:[&_.tag]:text-5.5 max-sm:[&_.tag]:block max-sm:[&_.tag]:ml-0 max-sm:[&_.tag]:mt-1">
          {slide.name}
          <span className="tag">{slide.taglineDisplay}</span>
        </h2>

        <p className="ab-banner__desc text-3.5 text-fg-muted leading-[1.55] max-w-165 line-clamp-2 ">
          {slide.description}
        </p>

        <div className="ab-banner__try flex items-center gap-2 flex-wrap mt-0.5">
          <span className="ab-banner__try-label font-mono text-2.5 tracking-[0.12em] uppercase text-fg-subtle mr-0.5">
            Try saying
          </span>
          {slide.prompts.map((p) => (
            <span
              key={p}
              className="ab-try-chip text-3 text-fg-muted bg-bg-base border border-border-subtle rounded-100 py-1 px-3 whitespace-nowrap"
            >
              {p}
            </span>
          ))}
        </div>

        <div className="ab-banner__foot flex items-center gap-3.5 flex-wrap mt-1.5">
          {live.state === "available" ? (
            // Not installed yet — the CTA IS the install, through the same
            // consent modal the grid cards open.
            <button
              type="button"
              className="ab-btn-notify [font-family:inherit] text-3.5 font-medium text-bg-base bg-fg-default border-none rounded-10 py-2.5 px-4 cursor-pointer"
              onClick={() => onInstall(live)}
            >
              {installing ? "Checking..." : "Install"}
            </button>
          ) : (
            <button
              type="button"
              className="ab-btn-notify [font-family:inherit] text-3.5 font-medium text-bg-base bg-fg-default border-none rounded-10 py-2.5 px-4 cursor-pointer"
              onClick={() => router.push(`/abilities/${slide.slug}`)}
              onMouseEnter={() => prefetchAbility(slide.slug)}
              onFocus={() => prefetchAbility(slide.slug)}
            >
              View ability
            </button>
          )}
          {slide.works && <WorksCoins srcs={slide.works} size="lg" />}
          {inv && (
            <b className="font-mono text-3 font-medium text-gold-ink">{inv}</b>
          )}
          <span className="ab-banner__meta font-mono text-3 text-fg-subtle [font-feature-settings:'tnum'] inline-flex items-center gap-1">
            {"by"}
            {slide.author.logo === "vestra" && (
              // alt="" — the author name follows in text, so a named mark
              // makes a screen reader say "Vestra Vestra".
              <VestraLogoMark
                size={13}
                alt=""
                className="ab-by-mark w-3.5 h-3.5 rounded-4"
              />
            )}
            {slide.author.logo === "image" && slide.author.logoUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={slide.author.logoUrl}
                alt=""
                aria-hidden
                className="ab-by-mark w-3.5 h-3.5 rounded-4"
              />
            )}
            {slide.author.name}
            {live.installed_count > 0
              ? ` · ${formatInstalls(live.installed_count)} installs`
              : null}
          </span>
        </div>
      </div>

      {slide.heroImageUrl ? (
        <div className="ab-banner__mark ab-banner__mark--img relative flex items-center justify-center shrink-0 w-82.5 h-52.5 rounded-none max-md:hidden [&_img]:max-w-full [&_img]:max-h-full [&_img]:[object-fit:contain] [&_img]:drop-shadow-abilities-tab">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={slide.heroImageUrl} alt={slide.name} draggable={false} />
        </div>
      ) : (
        <div className="ab-banner__mark ab-banner__mark--own relative w-47.5 h-47.5 rounded-32 flex items-center justify-center shrink-0 max-md:hidden bg-gold-molten-wash-hover border border-solid border-gold-molten-mid-22 shadow-abilities-tab-2 [&_svg]:w-21 [&_svg]:h-21 [&_svg]:[stroke:url(#ab-gild)] [&_svg]:[fill:none]">
          {slide.iconSvg}
        </div>
      )}
    </article>
  );
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

function EmptyState({ message }: { message: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center px-6 py-16
 rounded-16 border border-dashed border-border-subtle bg-bg-subtle"
    >
      <p className="text-3.5 text-fg-muted">{message}</p>
    </div>
  );
}
